import { computeCorrectScore, getCounts, getText } from "./helper";
import {
  calculateLinkDistributionScore,
  calculateLinkRatio,
  getLinks,
  linkingScoreWeightsWithSingleAnchor,
  linkingScoreWeights,
  linkingScoreWeightsWithCompetitor,
} from "./linkingHelper";

// External links check
const noOfExternalInternalLinks = (htmlContent, url) => {
  if (getText(htmlContent).trim().length == 0) {
    return {
      score: -1,
      percentage: 0,
      externalLinks: 0,
      internalLinks: 0,
    };
  }
  const internalDomains = ["/"];
  if (url.replace(/^https?:\/\//, "").split("/").length > 0) {
    internalDomains.push(url.replace(/^https?:\/\//, "").split("/")[0]);
  }

  const links = getLinks(htmlContent);
  const result = {
    internal: [],
    external: [],
  };

  links.forEach((link) => {
    let href = link.getAttribute("href");

    if (href) {
      href = href.replace(/^https?:\/\//, "");

      if (!href.startsWith("#")) {
        const isInternal = internalDomains.some(
          (domain) => href === domain || href.startsWith(domain)
        );

        if (isInternal) {
          result.internal.push(href);
        } else {
          result.external.push(href);
        }
      }
    }
  });

  const ratio_score = calculateLinkRatio(
    result.internal.length,
    result.external.length
  );
  const ratio =
    result.external.length === 0
      ? result.internal.length
      : result.internal.length / result.external.length;

  if (ratio_score === 0) {
    return {
      score: 0,
      percentage: parseFloat(ratio).toFixed(2),
      externalLinks: result.external.length,
      internalLinks: result.internal.length,
    };
  }
  if (ratio_score === 50) {
    return {
      score: 50,
      percentage: parseFloat(ratio).toFixed(2),
      externalLinks: result.external.length,
      internalLinks: result.internal.length,
    };
  }
  if (ratio_score === 100) {
    return {
      score: 100,
      percentage: parseFloat(ratio).toFixed(2),
      externalLinks: result.external.length,
      internalLinks: result.internal.length,
    };
  }
};

// Link distribution
const splitContentByH2 = (htmlContent) => {
  const sections = [];
  const parser = new DOMParser();
  const doc = parser.parseFromString(htmlContent, "text/html");

  let currentSection = { content: "" };

  doc.body.childNodes.forEach((node) => {
    if (node.tagName === "H2") {
      if (currentSection.content) {
        sections.push(currentSection);
      }
      currentSection = { title: node.textContent, content: "" };
    } else {
      if (node.outerHTML) {
        currentSection.content += node.outerHTML;
      }
    }
  });

  if (currentSection.content) {
    sections.push(currentSection);
  }

  return sections.map((x) => x.content);
};

// Links per section Check
const linksPerSectionCheck = (formikprops) => {
  const introText = getText(formikprops.values.introduction).trim();
  const intro = formikprops.values.introduction;
  const conslusionText = getText(formikprops.values.conclusion).trim();
  const conslusion = formikprops.values.conclusion;
  const contentText = getText(formikprops.values.content).trim();
  const content = formikprops.values.content;
  if (
    contentText.length == 0 &&
    introText.length == 0 &&
    conslusionText.length == 0
  ) {
    return -1;
  }
  let sections = splitContentByH2(content);
  if (introText.length > 0) {
    sections.push(intro);
  }
  if (conslusionText.length > 0) {
    sections.push(conslusion);
  }
  let sectionsWithLinks = [];
  let totalLinks = 0;
  let totalWords = 0;
  sections.forEach((content) => {
    const links = getLinks(content).length;
    totalLinks += links;
    const contentLength = getCounts(getText(content).trim()).words;
    totalWords += contentLength;
    if (contentLength > 0) {
      sectionsWithLinks.push(links / contentLength);
    } else {
      sectionsWithLinks.push(0);
    }
  });
  const score =
    sectionsWithLinks.reduce(
      (sum, num) => sum + Math.pow(num - totalLinks / totalWords, 2),
      0
    ) / sections.length;

  return calculateLinkDistributionScore(score);
};

// Score Calculation
export const startMeshLinksAnalysis = (formikprops, competitorResults) => {
  const links = noOfExternalInternalLinks(
    formikprops.values.introduction +
      formikprops.values.content +
      formikprops.values.conclusion,
    formikprops.values.url_path.trim()
  );
  const linkPlacement = linksPerSectionCheck(formikprops);

  let totalScore = 0;
  if (!competitorResults?.linkingTable) {
    totalScore = computeCorrectScore(
      [links.score, linkPlacement],
      linkingScoreWeights
    );
  } else {
    if (
      competitorResults?.linkingTable?.anchor_text_type_score != null &&
      competitorResults?.linkingTable?.anchor_length_score != null
    ) {
      totalScore = computeCorrectScore(
        [
          links.score,
          linkPlacement,
          competitorResults?.linkingTable?.anchor_length_score,
          competitorResults?.linkingTable?.anchor_text_type_score,
        ],
        linkingScoreWeightsWithCompetitor
      );
    }

    if (
      competitorResults?.linkingTable?.anchor_length_score == null &&
      competitorResults?.linkingTable?.anchor_text_type_score != null &&
      competitorResults?.linkingTable?.anchor_text_type_score != 0
    ) {
      totalScore = computeCorrectScore(
        [
          links.score,
          linkPlacement,
          competitorResults?.linkingTable?.anchor_length_score,
        ],
        linkingScoreWeightsWithSingleAnchor
      );
    }
    if (
      competitorResults?.linkingTable?.anchor_text_type_score == null &&
      competitorResults?.linkingTable?.anchor_length_score != null
    ) {
      totalScore = computeCorrectScore(
        [
          links.score,
          linkPlacement,
          competitorResults?.linkingTable?.anchor_text_type_score,
        ],

        linkingScoreWeightsWithSingleAnchor
      );
    }
    if (
      competitorResults?.linkingTable?.anchor_text_type_score == null &&
      (competitorResults?.linkingTable?.anchor_length_score == null ||
        competitorResults?.linkingTable?.anchor_text_type_score === 0)
    ) {
      totalScore = computeCorrectScore(
        [links.score, linkPlacement],
        linkingScoreWeights
      );
    }
  }

  return {
    total_score: Math.round(totalScore),
    nb_link: links,
    placement: linkPlacement,
  };
};
